import React, { useContext, useState } from "react";
import "../../../assets/css/seller/OrdersInSeller/sellerOrderDetail.css";
import { Container, Spinner } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import SellerMasterLayout from "../../../layout/seller/SellerMasterLayout";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import Swal from "sweetalert2";

export default function SellerOrderDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const orderData = location.state?.order;

  const { baseUrl } = useContext(AppContext);
  const [status, setStatus] = useState(orderData?.status || "pending");
  const [loading, setLoading] = useState(null); // "ongoing" | "completed" | "cancelled"

  if (!orderData) {
    return (
      <SellerMasterLayout>
        <p className="text-center py-5">No order data found.</p>
      </SellerMasterLayout>
    );
  }

  const subtotal = orderData.amount || 0;
  const taxRate = 0.05;
  const platformFeeRate = 0.1;
  const tax = Math.round(subtotal * taxRate);
  const platformFee = Math.round(subtotal * platformFeeRate);
  const payout = subtotal + tax - platformFee;

  const handleStatus = (newStatus) => {
    setLoading(newStatus);

    const formData = new FormData();
    formData.append("id", orderData.id);
    formData.append("status", newStatus);

    axios
      .post(`${baseUrl}/seller/orders/updateStatus`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then((response) => {
        console.log("Status Update Response:", response.data);

        setStatus(newStatus);
        setLoading(null);

        // ==========================================
        // ORDER COMPLETED SWEET ALERT
        // ==========================================
        if (newStatus === "complete") {
          Swal.fire({
            icon: "success",
            title: "Order Completed",
            text: `Order #${orderData.id} has been completed successfully.`,
            confirmButtonText: "OK",
            confirmButtonColor: "#28a745",
          });
        }

        // ==========================================
        // OPTIONAL: CANCEL ALERT
        // ==========================================
        else if (newStatus === "cancelled") {
          Swal.fire({
            icon: "success",
            title: "Order Cancelled",
            text: `Order #${orderData.id} has been cancelled.`,
            confirmButtonText: "OK",
          });
        }

        // ==========================================
        // OPTIONAL: ONGOING ALERT
        // ==========================================
        else if (newStatus === "ongoing") {
          Swal.fire({
            icon: "success",
            title: "Order Updated",
            text: `Order #${orderData.id} is now ongoing.`,
            confirmButtonText: "OK",
          });
        }

        // ==========================================
        // OPTIONAL: PENDING ALERT
        // ==========================================
        else if (newStatus === "pending") {
          Swal.fire({
            icon: "success",
            title: "Order Pending",
            text: `Order #${orderData.id} is now pending.`,
            confirmButtonText: "OK",
          });
        }
      })
      .catch((err) => {
        console.log("Update Status Error:", err?.response?.data || err);

        setLoading(null);

        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text:
            err?.response?.data?.message || "Unable to update order status.",
          confirmButtonText: "OK",
        });
      });
  };

  const getStatusLabel = (s) => {
    switch (s) {
      case "ongoing":
        return "Ongoing";

      case "complete":
        return "Completed";

      case "cancelled":
        return "Cancelled";

      default:
        return s;
    }
  };

  return (
    <SellerMasterLayout>
      <Container fluid>
        {/* Header */}
        <div className="sod-header">
          <button className="sod-back" onClick={() => window.history.back()}>
            &#8592;
          </button>
          <div className="sod-title-block">
            <h1 className="sod-title">Order #{orderData.id}</h1>
            <span className="sod-date">
              📅 Placed on{" "}
              {new Date(orderData.created_at).toLocaleDateString("en-GB")}
            </span>
          </div>
          <span className={`sod-status-badge ${status}`}>
            <span className="sod-badge-dot" />
            {getStatusLabel(status)}
          </span>
        </div>

        <div className="sod-body">
          {/* Left column */}
          <div className="sod-left">
            {/* Deal Items */}
            <div className="sod-card">
              <h2 className="sod-card-title">🎁 Deal Items</h2>
              <ul className="sod-items-list">
                {orderData.tpcarts?.length > 0 ? (
                  orderData.tpcarts.map((cart, index) => (
                    <li key={cart.id} className="sod-item-row">
                      <span className="sod-item-num">{index + 1}</span>
                      <div className="sod-item-info">
                        <span className="sod-item-name">
                          {cart.tpdeal?.deal_title || "N/A"}
                        </span>
                        <span className="sod-item-qty">
                          PKR {cart.tpdeal?.deal_price?.toLocaleString()}
                        </span>
                      </div>
                      <span className="sod-item-tag">Included</span>
                    </li>
                  ))
                ) : (
                  <p className="text-muted">No items found</p>
                )}
              </ul>
            </div>

            {/* Update Status */}
            <div className="sod-card">
              <h2 className="sod-card-title">Update Order Status</h2>
              <div className="sod-status-btns">
                <button
                  className={`sod-btn-status ongoing ${status === "ongoing" ? "active" : ""}`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("ongoing")}
                >
                  <span className="sod-btn-icon">▶</span>
                  {loading === "ongoing" ? "Updating..." : "Mark Ongoing"}
                </button>
                <button
                  className={`sod-btn-status completed ${status === "complete" ? "active" : ""}`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("complete")}
                >
                  <span className="sod-btn-icon">✓</span>
                  {loading === "complete" ? "Updating..." : "Mark Completed"}
                </button>
                <button
                  className={`sod-btn-status cancelled ${status === "cancelled" ? "active" : ""}`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("cancelled")}
                >
                  <span className="sod-btn-icon">✕</span>
                  {loading === "cancelled" ? "Updating..." : "Cancel Order"}
                </button>
              </div>
              <p className="sod-status-note">
                Customer is notified automatically whenever you update the
                status.
              </p>
            </div>
          </div>

          {/* Right column */}
          <div className="sod-right">
            {/* Customer */}
            {/* Customer & Shipping Address */}
            <div className="sod-card">
              <h2 className="sod-card-title">👤 Customer</h2>

              <p className="sod-customer-name">
                {orderData.shipping_name || "N/A"}
              </p>

              <p className="sod-customer-info">
                📞 {orderData.shipping_phone || "N/A"}
              </p>
            </div>

            <div className="sod-card">
              <h2 className="sod-card-title">📦 Shipping Address</h2>

              <p className="sod-customer-info">
                📍 {orderData.shipping_address || "N/A"}
              </p>

              <p className="sod-customer-info">
                🏙️ {orderData.shipping_city || "N/A"}
              </p>

              <p className="sod-customer-info">
                📮 {orderData.shipping_postal_code || "N/A"}
              </p>
            </div>

            {/* Shop */}
            {/* <div className="sod-card">
              <h2 className="sod-card-title">🏪 Shop</h2>
              <p className="sod-shop-name">
                {orderData.tpshop?.shop_name || "N/A"}
              </p>
              <p className="sod-shop-deal">
                Deal: {orderData.tpcarts?.[0]?.tpdeal?.deal_title || "N/A"}
              </p>
            </div> */}

            {/* Payment Summary */}
            {/* Update Status */}
            <div className="sod-card">
              <h2 className="sod-card-title">Update Order Status</h2>

              <div className="sod-status-btns">
                {/* PENDING */}
                <button
                  className={`sod-btn-status pending ${
                    status === "pending" ? "active" : ""
                  }`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("pending")}
                >
                  {loading === "pending" ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <span className="sod-btn-icon">⏳</span>
                      Mark Pending
                    </>
                  )}
                </button>

                {/* ONGOING */}
                <button
                  className={`sod-btn-status ongoing ${
                    status === "ongoing" ? "active" : ""
                  }`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("ongoing")}
                >
                  {loading === "ongoing" ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <span className="sod-btn-icon">▶</span>
                      Mark Ongoing
                    </>
                  )}
                </button>

                {/* COMPLETED */}
                <button
                  className={`sod-btn-status completed ${
                    status === "complete" ? "active" : ""
                  }`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("complete")}
                >
                  {loading === "complete" ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <span className="sod-btn-icon">✓</span>
                      Mark Completed
                    </>
                  )}
                </button>

                {/* CANCEL */}
                <button
                  className={`sod-btn-status cancelled ${
                    status === "cancelled" ? "active" : ""
                  }`}
                  disabled={loading !== null}
                  onClick={() => handleStatus("cancelled")}
                >
                  {loading === "cancelled" ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <span className="sod-btn-icon">✕</span>
                      Cancel Order
                    </>
                  )}
                </button>
              </div>

              <p className="sod-status-note">
                Customer is notified automatically whenever you update the
                status.
              </p>
            </div>
          </div>
        </div>
      </Container>
    </SellerMasterLayout>
  );
}
